<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useRoute } from 'vue-router';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const route = useRoute();

const inboxName = ref('');
const apiKey = ref('');
const selectedEntities = ref(['reviews']);

const ENTITY_OPTIONS = ['reviews', 'questions', 'chats', 'returns'];

const platform = computed(() => route.params.sub_page);
const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const v$ = useVuelidate(
  {
    inboxName: { required },
  },
  { inboxName }
);

async function createChannel() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  try {
    const inbox = await store.dispatch('inboxes/createChannel', {
      name: inboxName.value,
      channel: {
        type: 'marketplace',
        platform: platform.value,
        entities: selectedEntities.value,
        api_key: apiKey.value,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inbox.id,
      },
    });
  } catch (error) {
    useAlert(
      error.message || t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.API.ERROR_MESSAGE')
    );
  }
}
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.DESC')"
    />
    <form
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="createChannel()"
    >
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.INBOX_NAME.LABEL') }}
          <input
            v-model="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.INBOX_NAME.PLACEHOLDER')
            "
            @blur="v$.inboxName.$touch"
          />
        </label>
      </div>

      <div class="flex-shrink-0 flex-grow-0">
        <label>
          {{ $t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.API_KEY.LABEL') }}
          <input
            v-model="apiKey"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.API_KEY.PLACEHOLDER')
            "
          />
        </label>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ $t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.API_KEY.HELP') }}
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <p class="text-sm font-medium mb-2">
          {{ $t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.ENTITIES.LABEL') }}
        </p>
        <div class="flex flex-wrap gap-4">
          <label
            v-for="entity in ENTITY_OPTIONS"
            :key="entity"
            class="flex items-center gap-2 cursor-pointer"
          >
            <input v-model="selectedEntities" type="checkbox" :value="entity" />
            {{
              $t(
                `INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.ENTITIES.${entity.toUpperCase()}`
              )
            }}
          </label>
        </div>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.MARKETPLACE_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
